import Spinner from "../../shared/UxbiSpinner";
import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Table } from "react-bootstrap";
import {
  Download,
  ArrowLeft,
  ReceiptText,
  Wallet,
  CalendarClock,
  Eye,
  Landmark,
} from "lucide-react";
import { toast } from "react-toastify";
import { useSession } from "../../auth/SessionProvider";
import { requestBlob, request } from "../../api/http";
import useResource from "../../shared/useResource";
import { Header, Status, Pager, Field, money } from "../../shared/UI";
import RemoteSelect from "../../components/common/RemoteSelect";
import { buildListQuery } from "../../api/query";
import DocumentViewer from "../../shared/DocumentViewer";
export const reportCatalog = {
  ventas: {
    title: "Ventas",
    description: "Ventas netas por cliente y documento.",
    permission: "reporte-ventas",
    Icon: ReceiptText,
  },
  antiguedad: {
    title: "Antigüedad de saldos",
    description: "Cartera actual por cliente y vencimiento.",
    permission: "cartera",
    Icon: Wallet,
  },
  seguimiento: {
    title: "Seguimiento comercial",
    description: "Actividades y oportunidades por agente.",
    permission: "seguimientos",
    Icon: CalendarClock,
  },
  bancos: {
    title: "Bancos",
    description: "Consulta movimientos UXBI y del extracto bancario.",
    permission: "bancos",
    Icon: Landmark,
  },
};
export function ReportPicker() {
  const { session } = useSession(),
    nav = useNavigate();
  return (
    <>
      <Header
        title="Reportes"
        description="Elige la información que necesitas consultar."
      />
      <div className="report-grid">
        {Object.entries(reportCatalog)
          .filter(([, r]) => session.modules[r.permission])
          .map(([key, r]) => (
            <button
              className="report-option"
              key={key}
              onClick={() => nav(`/reportes/${key}`)}
            >
              <r.Icon size={28} />
              <h2>{r.title}</h2>
              <p>{r.description}</p>
              <span>Consultar reporte →</span>
            </button>
          ))}
      </div>
    </>
  );
}
export default function ReportPage({ mode }) {
  const { kind } = useParams(),
    [params] = useSearchParams(),
    nav = useNavigate(),
    { session } = useSession();
  const report = mode || kind;
  const cfg = reportCatalog[report] || {
    title:
      report === "estado-cuenta" ? "Estado de cuenta" : "Cuentas por cobrar",
  };
  const today = new Date().toLocaleDateString("en-CA");
  const [from, setFrom] = useState(
      params.get("desde") || today.slice(0, 8) + "01",
    ),
    [until, setUntil] = useState(params.get("hasta") || today),
    [cliente, setCliente] = useState(""),
    [agente, setAgente] = useState(""),
    [page, setPage] = useState(1),
    [exporting, setExporting] = useState(false),
    [view, setView] = useState(null);
  const realReport = report === "cartera" ? "antiguedad" : report;
  const q = new URLSearchParams({
    desde: from,
    hasta: until,
    cliente: cliente || "",
    agente: agente || "",
    page,
  });
  const path = `comercial-app/reportes/${realReport}?${q}`;
  const { data, loading, error, reload } = useResource(path);
  const exportFile = async () => {
    setExporting(true);
    try {
      const b = await requestBlob(
        `comercial-app/reportes/${realReport}/exportar?${q}`,
        { method: "POST" },
      );
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${realReport}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };
  const monetary = ["saldo", "monto", "importe"];
  const columns = Object.keys(data?.data?.[0] || {}).filter(
    (k) => !["id", "id_encabezado"].includes(k),
  );
  const lookup = (resource, search, page) =>
    request(
      `${resource}?lookup=1&${buildListQuery({
        page,
        limit: 15,
        filters: search
          ? [{ property: "nombre", operator: "LIKE", value: search }]
          : [],
      })}`,
    );
  const change = (setter) => (v) => {
    setter(v);
    setPage(1);
  };
  return (
    <>
      <Header
        title={cfg.title}
        description={cfg.description || "Consulta de cartera por cliente."}
      />
      {!mode && (
        <Button
          variant="link"
          className="back-link"
          onClick={() => nav("/reportes")}
        >
          <ArrowLeft size={17} /> Cambiar reporte
        </Button>
      )}
      <div className="surface report-filters">
        <div className="filters-grid">
          {["ventas", "seguimiento"].includes(realReport) && (
            <>
              <Field
                label="Desde"
                type="date"
                value={from}
                onChange={change(setFrom)}
              />
              <Field
                label="Hasta"
                type="date"
                value={until}
                onChange={change(setUntil)}
              />
            </>
          )}
          <RemoteSelect
            label="Cliente"
            value={cliente}
            onChange={(_, v) => change(setCliente)(v)}
            loadOptions={({ search, page }) => lookup("clientes", search, page)}
            loadById={(id) => request(`clientes/${id}?lookup=1`)}
            getOptionLabel={(x) => x.nombre}
          />
          {session.scope.all_agents && (
            <RemoteSelect
              label="Agente"
              value={agente}
              onChange={(_, v) => change(setAgente)(v)}
              loadOptions={({ search, page }) =>
                lookup("usuarios", search, page)
              }
              loadById={(id) => request(`usuarios/${id}`)}
              getOptionLabel={(x) => x.nombre}
            />
          )}
        </div>
        <div className="filter-actions">
          <Button
            variant="light"
            onClick={() => {
              setCliente("");
              setAgente("");
              setPage(1);
            }}
          >
            Limpiar filtros
          </Button>
          <Button
            variant="outline-primary"
            disabled={loading || exporting || !!error}
            onClick={exportFile}
          >
            {exporting ? (
              <Spinner size="sm" label="Exportando…" />
            ) : (
              <Download size={17} />
            )}
            {exporting ? "Exportando…" : "Exportar para Excel"}
          </Button>
        </div>
      </div>
      <Status loading={loading} error={error} onRetry={reload} />
      {data && !error && (
        <>
          <div className="report-totals">
            {Object.entries(data.totales || {}).map(([c, v]) => (
              <div key={c}>
                <small>
                  {realReport === "ventas" ? "Ventas netas" : "Saldo actual"} ·{" "}
                  {c}
                </small>
                <strong>{money(v, c)}</strong>
              </div>
            ))}
          </div>
          <p className="report-note">{data.nota}</p>
          <div className="desktop-data surface">
            <Table responsive hover>
              <thead>
                <tr>
                  {columns.map((k) => (
                    <th key={k}>{k.replaceAll("_", " ")}</th>
                  ))}
                  {realReport !== "seguimiento" && <th>Documento</th>}
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => (
                  <tr key={r.id}>
                    {columns.map((k) => (
                      <td key={k}>
                        {monetary.includes(k)
                          ? money(r[k], r.moneda)
                          : String(r[k] ?? "—")}
                      </td>
                    ))}
                    {realReport !== "seguimiento" && (
                      <td>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => setView(r.id_encabezado || r.id)}
                          title="Ver documento"
                        >
                          <Eye size={17} />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="mobile-data">
            {data.data.map((r) => (
              <article className="surface" key={r.id}>
                {columns.map((k) => (
                  <div className="mobile-pair" key={k}>
                    <span>{k.replaceAll("_", " ")}</span>
                    <strong>
                      {monetary.includes(k)
                        ? money(r[k], r.moneda)
                        : String(r[k] ?? "—")}
                    </strong>
                  </div>
                ))}
                {realReport !== "seguimiento" && (
                  <Button
                    className="w-100 mt-3"
                    variant="light"
                    onClick={() => setView(r.id_encabezado || r.id)}
                  >
                    <Eye size={17} /> Ver documento
                  </Button>
                )}
              </article>
            ))}
          </div>
          {!data.data.length && (
            <div className="empty surface">
              No hay registros con estos filtros.
            </div>
          )}
          <Pager data={data} page={page} setPage={setPage} />
        </>
      )}
      {view && <DocumentViewer id={view} onClose={() => setView(null)} />}
    </>
  );
}
